import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  format, parse, startOfWeek, getDay, isSameDay, differenceInCalendarDays,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import axios from 'axios';

// ── Localizer ──────────────────────────────────────────────────────────────
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const API_URL = '';

// ── Timezone-safe parsing ──────────────────────────────────────────────────
const parseLocal = (dateStr) => {
  if (!dateStr) return new Date();
  const s = dateStr.replace(' ', 'T').replace(/\+.*$|Z$/, '');
  const [datePart, timePart = '00:00:00'] = s.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

// ── Helpers ────────────────────────────────────────────────────────────────
const isMultiDay = (event) => {
  const start = parseLocal(event.start_time);
  const end   = parseLocal(event.end_time);
  const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0;
  return differenceInCalendarDays(end, start) >= 1 && !endsAtMidnight;
};

const toRbcEvent = (event) => ({
  ...event,
  start: parseLocal(event.start_time),
  end:   parseLocal(event.end_time),
});

const toInputDT = (date) => format(date, "yyyy-MM-dd'T'HH:mm");

// Expand a recurring event into a calendar event for a specific year.
// Returns null for Feb 29 on non-leap years.
const expandRecurring = (rec, year) => {
  if (rec.month === 2 && rec.day === 29) {
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (!isLeap) return null;
  }
  const date = new Date(year, rec.month - 1, rec.day, 0, 0, 0);
  return {
    ...rec,
    recurring: true,
    start: date,
    end:   new Date(year, rec.month - 1, rec.day, 23, 59, 59),
    allDay: true,
  };
};

// Generate recurring calendar events for a range of years so navigating
// months never drops them off the calendar.
const expandRecurringEvents = (recurringEvents, viewYear) => {
  const results = [];
  for (const rec of recurringEvents) {
    for (const y of [viewYear - 1, viewYear, viewYear + 1]) {
      const ev = expandRecurring(rec, y);
      if (ev) results.push(ev);
    }
  }
  return results;
};

// Calculate age or years together label
const getOccasionLabel = (rec, eventYear) => {
  if (!rec.start_year) return null;
  const count = eventYear - rec.start_year;
  if (count <= 0) return null;
  if (rec.type === 'birthday') return `${count} years old`;
  if (rec.type === 'anniversary') return `${count} year${count === 1 ? '' : 's'}`;
  return null;
};

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// ── Component ──────────────────────────────────────────────────────────────
function App() {
  const [isLoggedIn, setIsLoggedIn]   = useState(false);
  const [user, setUser]               = useState(null);
  const [token, setToken]             = useState(localStorage.getItem('token'));
  const [events, setEvents]           = useState([]);
  const [recurringEvents, setRecurringEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewYear, setViewYear]         = useState(new Date().getFullYear());

  // Modals
  const [showEventModal, setShowEventModal]         = useState(false);
  const [showAuthModal, setShowAuthModal]           = useState(false);
  const [showOccasionsModal, setShowOccasionsModal] = useState(false);
  const [isRegistering, setIsRegistering]           = useState(false);
  const [editingEvent, setEditingEvent]             = useState(null);
  const [editingOccasion, setEditingOccasion]       = useState(null);
  const [viewMode, setViewMode] = useState(localStorage.getItem('viewMode') || 'my');
  const [menuOpen, setMenuOpen] = useState(false);

  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [eventForm, setEventForm] = useState({
    title: '', description: '', start_time: '', end_time: '', location: '',
  });
  const emptyOccasionForm = { title: '', type: 'birthday', month: 1, day: 1, start_year: '', notes: '' };
  const [occasionForm, setOccasionForm] = useState(emptyOccasionForm);

  // ── Fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      fetchEvents();
      fetchRecurringEvents();
    } else {
      setShowAuthModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewMode]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setUser(res.data[0]);
      setIsLoggedIn(true);
    } catch (err) {
      handleLogout();
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events?view=${viewMode}`);
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchRecurringEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/recurring-events`);
      setRecurringEvents(res.data);
    } catch (err) {
      console.error('Error fetching recurring events:', err);
    }
  };

  // ── Auth ───────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/login`, {
        email: authForm.email, password: authForm.password,
      });
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      setIsLoggedIn(true);
      setShowAuthModal(false);
      setAuthForm({ name: '', email: '', password: '' });
    } catch (err) {
      alert('Login failed: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/register`, authForm);
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      setIsLoggedIn(true);
      setShowAuthModal(false);
      setAuthForm({ name: '', email: '', password: '' });
    } catch (err) {
      alert('Registration failed: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null); setUser(null); setIsLoggedIn(false);
    setShowAuthModal(true);
    delete axios.defaults.headers.common['Authorization'];
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'my' ? 'family' : 'my';
    setViewMode(newMode);
    localStorage.setItem('viewMode', newMode);
  };

  // ── Regular event CRUD ────────────────────────────────────────────────
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent.id}`, eventForm);
      } else {
        await axios.post(`${API_URL}/api/events`, eventForm);
      }
      await fetchEvents();
      setShowEventModal(false);
      setEditingEvent(null);
      setEventForm({ title: '', description: '', start_time: '', end_time: '', location: '' });
    } catch (err) {
      alert('Failed to save event: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`);
      await fetchEvents();
    } catch (err) {
      alert('Failed to delete event: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title, description: event.description || '',
      start_time: toInputDT(parseLocal(event.start_time)),
      end_time:   toInputDT(parseLocal(event.end_time)),
      location: event.location || '',
    });
    setShowEventModal(true);
  };

  const openNewEventModal = useCallback(({ start, end }) => {
    setEditingEvent(null);
    const startDT = new Date(start); startDT.setHours(9, 0, 0, 0);
    const endDT   = new Date(start);
    if (differenceInCalendarDays(end, start) > 0) { endDT.setTime(end.getTime()); }
    else { endDT.setHours(10, 0, 0, 0); }
    setEventForm({ title: '', description: '', start_time: toInputDT(startDT), end_time: toInputDT(endDT), location: '' });
    setSelectedDate(start);
    setShowEventModal(true);
  }, []);

  // ── Occasions (recurring) CRUD ────────────────────────────────────────
  const handleSaveOccasion = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...occasionForm,
        month: Number(occasionForm.month),
        day:   Number(occasionForm.day),
        start_year: occasionForm.start_year ? Number(occasionForm.start_year) : null,
      };
      if (editingOccasion) {
        await axios.put(`${API_URL}/api/recurring-events/${editingOccasion.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/recurring-events`, payload);
      }
      await fetchRecurringEvents();
      setEditingOccasion(null);
      setOccasionForm(emptyOccasionForm);
    } catch (err) {
      alert('Failed to save occasion: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteOccasion = async (id) => {
    if (!window.confirm('Delete this occasion?')) return;
    try {
      await axios.delete(`${API_URL}/api/recurring-events/${id}`);
      await fetchRecurringEvents();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const openEditOccasion = (occ) => {
    setEditingOccasion(occ);
    setOccasionForm({
      title: occ.title, type: occ.type, month: occ.month, day: occ.day,
      start_year: occ.start_year || '', notes: occ.notes || '',
    });
  };

  // ── Calendar interaction ──────────────────────────────────────────────
  const handleSelectSlot = useCallback(({ start, end, action }) => {
    if (action === 'doubleClick' || action === 'select') {
      openNewEventModal({ start, end });
    } else {
      setSelectedDate(start);
    }
  }, [openNewEventModal]);

  const handleSelectEvent = useCallback((rbcEvent) => {
    setSelectedDate(rbcEvent.start);
  }, []);

  const handleNavigate = useCallback((date) => {
    setSelectedDate(date);
    setViewYear(date.getFullYear());
  }, []);

  const eventPropGetter = useCallback((rbcEvent) => {
    if (rbcEvent.recurring) {
      const style = rbcEvent.type === 'birthday'
        ? { backgroundColor: '#ec4899', borderColor: '#db2777' }
        : { backgroundColor: '#f43f5e', borderColor: '#e11d48' };
      return { style: { ...style, color: 'white', borderRadius: '4px' } };
    }
    const isOwn = rbcEvent.creator_id === user?.id;
    return {
      style: {
        backgroundColor: isOwn ? '#667eea' : '#10b981',
        borderColor:     isOwn ? '#5568d3' : '#059669',
        color: 'white', borderRadius: '4px',
      },
    };
  }, [user]);

  // ── Derived data ──────────────────────────────────────────────────────
  const allCalendarEvents = [
    ...events.map(toRbcEvent),
    ...expandRecurringEvents(recurringEvents, viewYear),
  ];

  const regularForDate = events.filter((ev) => {
    const start = parseLocal(ev.start_time);
    const end   = parseLocal(ev.end_time);
    return isSameDay(start, selectedDate) || (start < selectedDate && end > selectedDate);
  });

  const recurringForDate = recurringEvents.filter((rec) =>
    rec.month - 1 === selectedDate.getMonth() && rec.day === selectedDate.getDate()
  );

  // ── Auth screen ───────────────────────────────────────────────────────
  if (!isLoggedIn && showAuthModal) {
    return (
      <div className="modal-overlay">
        <div className="modal auth-modal">
          <h2>{isRegistering ? 'Register' : 'Login'}</h2>
          <form onSubmit={isRegistering ? handleRegister : handleLogin}>
            {isRegistering && (
              <input type="text" placeholder="Name" value={authForm.name}
                onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} required />
            )}
            <input type="email" placeholder="Email" value={authForm.email}
              onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required />
            <input type="password" placeholder="Password" value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} required />
            <button type="submit">{isRegistering ? 'Register' : 'Login'}</button>
          </form>
          <p className="auth-switch">
            {isRegistering ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => setIsRegistering(!isRegistering)}>
              {isRegistering ? 'Login' : 'Register'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ── Main UI ───────────────────────────────────────────────────────────
  return (
    <div className="App">
      <header className="app-header">
        <h1>📅 Family Calendar</h1>
        {user && (
          <>
            {/* Desktop nav */}
            <div className="user-info desktop-nav">
              <div className="view-toggle">
                <button className={viewMode === 'my' ? 'active' : ''} onClick={toggleViewMode}>
                  {viewMode === 'my' ? '👤 My Events' : '👨‍👩‍👧‍👦 Family Events'}
                </button>
              </div>
              <button className="occasions-btn" onClick={() => setShowOccasionsModal(true)}>
                🎂 Occasions
              </button>
              <span>Welcome, {user.name}!</span>
              <button onClick={handleLogout}>Logout</button>
            </div>

            {/* Mobile hamburger */}
            <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu">
              {menuOpen ? '✕' : '☰'}
            </button>
          </>
        )}
      </header>

      {/* Mobile dropdown menu */}
      {user && menuOpen && (
        <div className="mobile-menu">
          <div className="view-toggle mobile-view-toggle">
            <button className={viewMode === 'my' ? 'active' : ''}
              onClick={() => { toggleViewMode(); setMenuOpen(false); }}>
              {viewMode === 'my' ? '👤 My Events' : '👨‍👩‍👧‍👦 Family Events'}
            </button>
          </div>
          <button className="occasions-btn mobile-menu-btn"
            onClick={() => { setShowOccasionsModal(true); setMenuOpen(false); }}>
            🎂 Occasions
          </button>
          <span className="mobile-welcome">Welcome, {user.name}!</span>
          <button className="mobile-logout-btn" onClick={() => { handleLogout(); setMenuOpen(false); }}>
            Logout
          </button>
        </div>
      )}

      <div className="calendar-container">
        {/* ── Left: Calendar ─────────────────────────────────────── */}
        <div className="calendar-section">
          <div className="rbc-wrapper">
            <Calendar
              localizer={localizer}
              events={allCalendarEvents}
              startAccessor="start"
              endAccessor="end"
              views={{ month: true }}
              defaultView="month"
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventPropGetter}
              onNavigate={handleNavigate}
              style={{ height: '100%' }}
            />
          </div>
          <button className="add-event-btn"
            onClick={() => openNewEventModal({ start: selectedDate, end: selectedDate })}>
            + Add Event
          </button>
          <p className="calendar-hint">
            Click a day to see events · Double-click or drag to create a new event
          </p>
          <div className="calendar-legend">
            <span className="legend-item"><span className="legend-dot" style={{ background: '#667eea' }} />My events</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#10b981' }} />Family events</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#ec4899' }} />Birthdays</span>
            <span className="legend-item"><span className="legend-dot" style={{ background: '#f43f5e' }} />Anniversaries</span>
          </div>
        </div>

        {/* ── Right: Events panel ─────────────────────────────────── */}
        <div className="events-section">
          <h2>Events for {format(selectedDate, 'MMMM d, yyyy')}</h2>
          <div className="events-list">

            {/* Recurring occasions for this date */}
            {recurringForDate.map((rec) => {
              const label = getOccasionLabel(rec, selectedDate.getFullYear());
              return (
                <div key={`rec-${rec.id}`} className={`event-card occasion-card occasion-${rec.type}`}>
                  <div className="event-header">
                    <h3>{rec.type === 'birthday' ? '🎂' : '💕'} {rec.title}</h3>
                    <div className="event-badges">
                      <span className={`occasion-badge occasion-badge-${rec.type}`}>
                        {rec.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                      </span>
                    </div>
                  </div>
                  {label && <p className="occasion-label">{label}</p>}
                  {rec.notes && <p className="event-description">{rec.notes}</p>}
                  <p className="event-creator">Added by: {rec.creator_name}</p>
                </div>
              );
            })}

            {/* Regular events for this date */}
            {regularForDate.map((event) => {
              const multiDay = isMultiDay(event);
              return (
                <div key={event.id}
                  className={`event-card ${event.creator_id === user?.id ? 'my-event' : 'other-event'}`}>
                  <div className="event-header">
                    <h3>{event.title}</h3>
                    <div className="event-badges">
                      {viewMode === 'family' && (
                        <span className="event-owner-badge">
                          {event.creator_id === user?.id ? 'You' : event.creator_name}
                        </span>
                      )}
                      {multiDay && (
                        <span className="multi-day-badge">
                          {differenceInCalendarDays(parseLocal(event.end_time), parseLocal(event.start_time))} days
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="event-time">
                    {multiDay ? (
                      <>{format(parseLocal(event.start_time), 'MMM d, h:mm a')} {' → '} {format(parseLocal(event.end_time), 'MMM d, h:mm a')}</>
                    ) : (
                      <>{format(parseLocal(event.start_time), 'h:mm a')} {' – '} {format(parseLocal(event.end_time), 'h:mm a')}</>
                    )}
                  </p>
                  {event.description && <p className="event-description">{event.description}</p>}
                  {event.location && <p className="event-location">📍 {event.location}</p>}
                  {viewMode === 'my' && <p className="event-creator">Created by: {event.creator_name}</p>}
                  {event.creator_id === user?.id && (
                    <div className="event-actions">
                      <button onClick={() => openEditModal(event)}>Edit</button>
                      <button onClick={() => handleDeleteEvent(event.id)}>Delete</button>
                    </div>
                  )}
                </div>
              );
            })}

            {recurringForDate.length === 0 && regularForDate.length === 0 && (
              <p className="no-events">No events for this day</p>
            )}
          </div>
        </div>
      </div>

      {/* ── New/edit event modal ────────────────────────────────── */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingEvent ? 'Edit Event' : 'New Event'}</h2>
            <form onSubmit={handleSaveEvent}>
              <input type="text" placeholder="Event Title" value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} required />
              <textarea placeholder="Description" value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} />
              <label className="form-label">Start</label>
              <input type="datetime-local" value={eventForm.start_time}
                onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })} required />
              <label className="form-label">End</label>
              <input type="datetime-local" value={eventForm.end_time}
                onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })} required />
              <input type="text" placeholder="Location" value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} />
              <div className="modal-actions">
                <button type="submit">{editingEvent ? 'Update' : 'Create'}</button>
                <button type="button" onClick={() => setShowEventModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Occasions management modal ──────────────────────────── */}
      {showOccasionsModal && (
        <div className="modal-overlay" onClick={() => { setShowOccasionsModal(false); setEditingOccasion(null); setOccasionForm(emptyOccasionForm); }}>
          <div className="modal occasions-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎂 Birthdays &amp; Anniversaries</h2>

            {/* Add / edit form */}
            <div className="occasions-form-section">
              <h3>{editingOccasion ? 'Edit Occasion' : 'Add New Occasion'}</h3>
              <form onSubmit={handleSaveOccasion} className="occasions-form">
                <div className="form-row">
                  <input type="text" placeholder="Name / Title" value={occasionForm.title}
                    onChange={(e) => setOccasionForm({ ...occasionForm, title: e.target.value })} required />
                  <select value={occasionForm.type}
                    onChange={(e) => setOccasionForm({ ...occasionForm, type: e.target.value })}>
                    <option value="birthday">🎂 Birthday</option>
                    <option value="anniversary">💕 Anniversary</option>
                  </select>
                </div>
                <div className="form-row">
                  <select value={occasionForm.month}
                    onChange={(e) => setOccasionForm({ ...occasionForm, month: Number(e.target.value) })}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={occasionForm.day}
                    onChange={(e) => setOccasionForm({ ...occasionForm, day: Number(e.target.value) })}>
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <input type="number" placeholder={occasionForm.type === 'birthday' ? 'Birth year (optional)' : 'Start year (optional)'}
                    value={occasionForm.start_year}
                    min="1900" max={new Date().getFullYear()}
                    onChange={(e) => setOccasionForm({ ...occasionForm, start_year: e.target.value })} />
                </div>
                <textarea placeholder="Notes (optional)" value={occasionForm.notes}
                  onChange={(e) => setOccasionForm({ ...occasionForm, notes: e.target.value })} />
                <div className="modal-actions">
                  <button type="submit">{editingOccasion ? 'Update' : 'Add'}</button>
                  {editingOccasion && (
                    <button type="button" onClick={() => { setEditingOccasion(null); setOccasionForm(emptyOccasionForm); }}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* List of saved occasions */}
            <div className="occasions-list">
              {recurringEvents.length === 0 ? (
                <p className="no-events">No birthdays or anniversaries yet</p>
              ) : (
                recurringEvents.map((occ) => (
                  <div key={occ.id} className={`occasion-list-item occasion-list-${occ.type}`}>
                    <div className="occasion-list-info">
                      <span className="occasion-list-icon">{occ.type === 'birthday' ? '🎂' : '💕'}</span>
                      <div>
                        <strong>{occ.title}</strong>
                        <span className="occasion-list-date">
                          {MONTHS[occ.month - 1]} {occ.day}
                          {occ.start_year ? ` · born ${occ.start_year}` : ''}
                        </span>
                        {occ.notes && <span className="occasion-list-notes">{occ.notes}</span>}
                      </div>
                    </div>
                    <div className="occasion-list-actions">
                      <button onClick={() => openEditOccasion(occ)}>Edit</button>
                      <button onClick={() => handleDeleteOccasion(occ.id)}>Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: '1rem' }}>
              <button type="button" onClick={() => { setShowOccasionsModal(false); setEditingOccasion(null); setOccasionForm(emptyOccasionForm); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;